import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getItem, setItem, StorageItem } from '@app/@core/utils';
import { AttrItem } from '@app/models/attr-item';
import { Brand } from '@app/models/brand';
import { CateCount } from '@app/models/cate-count';
import { Cate } from '@app/models/category/cate';
import { CateAttribute } from '@app/models/category/cate-attribute';
import { CodedBrandRule } from '@app/models/coded-brand-rule';
import { EchartData } from '@app/models/common/echart-data';
import { MaskInfo } from '@app/models/common/mask-info';
import { OptionItem } from '@app/models/common/option-item';
import { Result } from '@app/models/common/result';
import { FeatCfgItem, FeatCfgParams } from '@app/models/contract-feat-cfg';
import { mbdInfo } from '@app/models/customizeMBD/mbd-info';
import { FactKey } from '@app/models/enums/fact-key.enum';
import { FeatDimType } from '@app/models/enums/feat-dim-type.enum';
import { FilterDimType, KolMetricsType } from '@app/models/enums/filter-dim-type.enum';
import { ImpDataType } from '@app/models/enums/imp-data-type';
import { PeriodDimType, SnapshotDimType } from '@app/models/enums/period-dim-type.enum';
import { EtailerBrand } from '@app/models/etailer-brand';
import { FeatureSubsc } from '@app/models/feature-subsc';
import { ItemSearchCriteria } from '@app/models/item-search-criteria';
import { JobInfo } from '@app/models/job-info';
import { ProgressType } from '@app/models/job-task';
import { KeywordRule } from '@app/models/keyword-rule';
import { BrandStats } from '@app/models/kol-brand-table';
import { kolCriteria, KolMetrics, kolOverview, KolProds, payloadItem } from '@app/models/kol-cfg';
import { KolBrandChart, KolBrandList } from '@app/models/kol-page-cfg';
import { allCataData, ksCataData, tiktokCataData } from '@app/models/kol-table';
import { MktSummary } from '@app/models/mkt-summary';
import { PagingData } from '@app/models/paging-data';
import { PltCates } from '@app/models/plt-cates';
import { PriceTier } from '@app/models/price-tier';
import { ProdItem } from '@app/models/prod-item';
import { SegmentConfig } from '@app/models/segment-config';
import { CrossCate } from '@app/models/settings/cross-cate';
import { FactInfo } from '@app/models/settings/fact-info';
import { HotwordItem } from '@app/models/settings/hotwordItem';
import { Market } from '@app/models/settings/market';
import { User } from '@app/models/settings/user';
import { SnapshotItem } from '@app/models/snapshot-item';
import { StoreItem } from '@app/models/store-item';
import { SummaryItem } from '@app/models/summary-item';
import { TemplateBaseInfo } from '@app/models/template-base-info';
import { TemplateConfig } from '@app/models/template-config';
import { TopskuItem } from '@app/models/top-sku-item';
import { TrendChartCriteria } from '@app/models/trend-chart-criteria';
import { UserPref } from '@app/models/user-pref';
import { LocaleLang } from '@core/services/i18n/locale-lang.enum';
import { environment } from '@environments/environment';
import { groupBy, pick } from 'lodash';
import { NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import qs from 'qs';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EcommerceService {
  public lastTplActionSubject: BehaviorSubject<TemplateConfig>;
  // public lastTplActionObservable: Observable<TemplateConfig>;
  public maskSubject: BehaviorSubject<MaskInfo> = new BehaviorSubject<MaskInfo>({ show: false });

  constructor(private http: HttpClient) {
    this.lastTplActionSubject = new BehaviorSubject<TemplateConfig>(JSON.parse(<string>getItem(StorageItem.LastTplCfg) || '{}'));
  }

  public get lastTplAction(): TemplateConfig {
    return this.lastTplActionSubject.value;
  }

  getUserPrefs(): Observable<UserPref[]> {
    const url = `${environment.apiUrl}/user/pref/list`;
    return this.http.get<Result<UserPref[]>>(url).pipe(
      map(res => {
        const data: UserPref[] = res.data;
        return data;
      }),
    );
  }

  saveOrUpdateUserPref(prefKey: string, prefContent: Record<string, any>): Observable<boolean> {
    const url = `${environment.apiUrl}/user/pref/save`;
    const payload = { prefKey, prefContent };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  getStarredTpls(): Observable<Array<TemplateConfig>> {
    const url = `${environment.apiUrl}/tpl/getStarredTpls`;
    return this.http.get<Result<Array<TemplateConfig>>>(url).pipe(map(res => res.data));
  }

  getTemplates(): Observable<Array<TemplateConfig>> {
    const url = `${environment.apiUrl}/tpl/list`;
    return this.http.get<Result<Array<TemplateConfig>>>(url).pipe(map(res => res.data));
  }

  getSelfTemplateIds(): Observable<Array<string>> {
    const url = `${environment.apiUrl}/tpl/getSelfTplIds`;
    return this.http.get<Result<Array<string>>>(url).pipe(map(res => res.data));
  }

  getFeatSubscInfo(templateId: string): Observable<Record<FeatDimType, FeatureSubsc>> {
    const url = `${environment.apiUrl}/contract/${templateId}/featSubscInfo`;
    return this.http.get<Result<Record<FeatDimType, FeatureSubsc>>>(url).pipe(map(res => res.data));
  }

  saveLastTplAction(templateConfig: TemplateConfig) {
    setItem(StorageItem.LastTplCfg, JSON.stringify(templateConfig));
    this.lastTplActionSubject.next(templateConfig);
  }

  getTemplateDetails(templateId: string): Observable<TemplateConfig> {
    const url = `${environment.apiUrl}/tpl/detail/${templateId}`;
    return this.http.get<Result<TemplateConfig>>(url).pipe(
      map(res => {
        const { data } = res;
        if (!data.categoryRules) {
          data.categoryRules = [];
        }
        return data;
      }),
    );
  }

  getTemplateBaseInfo(templateId: string): Observable<TemplateBaseInfo> {
    const url = `${environment.apiUrl}/tpl/${templateId}`;
    return this.http.get<Result<TemplateBaseInfo>>(url).pipe(map(res => res.data));
  }

  saveChanges(tplCfg: TemplateConfig): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/updateRules`;
    const payload = pick(tplCfg, ['templateId', 'keywordRules', 'categoryRules', 'superCateRules']);
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  renameTpl(templateId: string, templateName: string): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/rename`;
    const payload = { templateId, templateName };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  starOrUnstarTpl(templateId: string, isStar: boolean): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/setStar`;
    const payload = { templateId, starred: isStar };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  createNewTpl(templateName: string, tplType: string): Observable<string> {
    const url = `${environment.apiUrl}/tpl/createNew`;
    const payload = { templateName, extraInfo: { tplType } };
    return this.http.post<Result<string>>(url, payload).pipe(map(res => res.data));
  }

  switchToFullSet(templateId: string): Observable<TemplateBaseInfo> {
    const url = `${environment.apiUrl}/tpl/switchToFullSet`;
    const payload = { templateId };
    return this.http.post<Result<TemplateBaseInfo>>(url, payload).pipe(map(res => res.data));
  }

  deleteArchiveFilters(templateId: string): Observable<any> {
    const url = `${environment.apiUrl}/tpl/${templateId}/archiveFilters`;
    return this.http.delete<Result<any>>(url).pipe(map(res => res.data));
  }

  getPltCates(templateId: string): Observable<Array<PltCates>> {
    const url = `${environment.apiUrl}/ecat/list/${templateId}`;
    return this.http.get<Result<Array<PltCates>>>(url).pipe(map(res => res.data));
  }

  getMarkets(): Observable<Array<Market>> {
    const url = `${environment.apiUrl}/mkt/list`;
    return this.http.get<Result<Array<Market>>>(url).pipe(map(res => res.data));
  }

  getOfflineMbdData(templateId: string): Observable<Record<string, string[]>> {
    const url = `${environment.apiUrl}/mkt/offline?tplid=${templateId}`;
    return this.http.get<Result<Record<string, string[]>>>(url).pipe(map(res => res.data));
  }

  getMarketsByTpl(templateId: string): Observable<Array<Market>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/markets`;
    return this.http.get<Result<Array<Market>>>(url).pipe(map(res => res.data));
  }

  search(
    criteria: ItemSearchCriteria,
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
  ): Observable<PagingData<ProdItem>> {
    const url = `${environment.apiUrl}/item/search`;
    const payload = {
      ...criteria,
      pageIndex,
      pageSize,
      sortDirection: sortOrder === 'ascend' ? 'ASC' : sortOrder === 'descend' ? 'DESC' : null,
      sortColumn: sortField,
    };
    return this.http.post<Result<PagingData<ProdItem>>>(url, payload).pipe(map(res => res.data));
  }
  searchSummary(criteria: ItemSearchCriteria): Observable<ProdItem> {
    const url = `${environment.apiUrl}/item/summary`;
    return this.http.post<Result<ProdItem>>(url, criteria).pipe(map(res => res.data));
  }

  searchThumbnail(criteria: ItemSearchCriteria): Observable<Record<string, EchartData>> {
    const url = `${environment.apiUrl}/item/trend/thumbnail`;
    return this.http.post<Result<Record<string, EchartData>>>(url, criteria).pipe(map(res => res.data));
  }

  storeThumbnail(criteria: ItemSearchCriteria): Observable<Record<string, EchartData>> {
    const url = `${environment.apiUrl}/item/store/trend/thumbnail`;
    return this.http.post<Result<Record<string, EchartData>>>(url, criteria).pipe(map(res => res.data));
  }

  searchStore(
    criteria: ItemSearchCriteria,
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
  ): Observable<PagingData<StoreItem>> {
    const url = `${environment.apiUrl}/item/searchStore`;
    const payload = {
      ...criteria,
      pageIndex,
      pageSize,
      sortDirection: sortOrder === 'ascend' ? 'ASC' : sortOrder === 'descend' ? 'DESC' : null,
      sortColumn: sortField,
    };
    return this.http.post<Result<PagingData<StoreItem>>>(url, payload).pipe(map(res => res.data));
  }
  public currFactsSubject: BehaviorSubject<FactInfo[]> = new BehaviorSubject<FactInfo[]>([]);
  getContractFeatCfg(payload: FeatCfgParams): Observable<FeatCfgItem> {
    const url = `${environment.apiUrl}/contract/featCfg`;
    return this.http.post<Result<FeatCfgItem>>(url, payload).pipe(
      map(res => {
        const data = res.data;
        this.currFactsSubject.next(data.facts);
        return data;
      }),
    );
  }

  getPltCatItemCount(criteria: ItemSearchCriteria): Observable<Record<string, Array<CateCount>>> {
    const url = `${environment.apiUrl}/item/getPltCatMappingWithOrder`;
    return this.http.post<Result<Record<string, Array<CateCount>>>>(url, criteria).pipe(map(res => res.data));
  }

  getHotwordList(criteria: ItemSearchCriteria): Observable<Record<string, any>> {
    const url = `${environment.apiUrl}/hotword/list`;
    return this.http.post<Result<Record<string, any>>>(url, criteria).pipe(map(res => res.data));
  }

  getHotwordTrendChart(payload: TrendChartCriteria): Observable<Record<PeriodDimType, EchartData>> {
    const url = `${environment.apiUrl}/hotword/chart/trend`;
    return this.http.post<Result<Record<PeriodDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }

  getCrossCateChart(payload: TrendChartCriteria): Observable<Array<CrossCate>> {
    const url = `${environment.apiUrl}/hotword/crossCat`;
    return this.http.post<Result<Array<CrossCate>>>(url, payload).pipe(map(res => res.data));
  }

  getHotwordItems(payload: TrendChartCriteria): Observable<Array<HotwordItem>> {
    const url = `${environment.apiUrl}/hotword/items`;
    return this.http.post<Result<Array<HotwordItem>>>(url, payload).pipe(map(res => res.data));
  }

  getTrendChart(criteria: ItemSearchCriteria, facts: string, isSplitByPlt: boolean): Observable<Record<PeriodDimType, EchartData>> {
    const url = `${environment.apiUrl}/trend/charts`;
    const payload = this.getPayloadForTrend(criteria, facts, isSplitByPlt);
    // this.saveLastTplAction(lastActionTpl);
    return this.http.post<Result<Record<PeriodDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }

  getTrendChartForProvince(criteria: ItemSearchCriteria, facts: string, isSplitByPlt: boolean): Observable<Record<PeriodDimType, EchartData>> {
    const url = `${environment.apiUrl}/trend/provinceCharts`;
    const payload = this.getPayloadForTrend(criteria, facts, isSplitByPlt);
    // this.saveLastTplAction(lastActionTpl);
    return this.http.post<Result<Record<PeriodDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }
  getPayloadForTrend(criteria: ItemSearchCriteria, facts: string, isSplitByPlt: boolean) {
    const factTypes = facts ? facts.split(',') : [];
    const payload = {
      ...criteria,
      factTypes,
      isSplitByPlt,
    };
    const lastActionTpl = this.lastTplAction;
    lastActionTpl.trend = { factTypes, market: criteria.market, periods: criteria.periods };
    return payload;
  }

  getSearchTrendChart(criteria: ItemSearchCriteria, facts: string): Observable<Record<PeriodDimType, EchartData>> {
    const url = `${environment.apiUrl}/item/trend`;
    const factTypes = facts ? facts.split(',') : [];
    const payload = {
      ...criteria,
      factTypes,
    };
    return this.http.post<Result<Record<PeriodDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }

  getKolTrendChart(criteria: kolCriteria, urlPath: string): Observable<Record<PeriodDimType, EchartData>> {
    const url = `${environment.apiUrl}${urlPath}`;
    return this.http.post<Result<Record<PeriodDimType, EchartData>>>(url, criteria).pipe(map(res => res.data));
  }

  getBrandTrendChart(criteria: SnapshotItem, factTypes: string[]): Observable<Record<SnapshotDimType, EchartData>> {
    const url = `${environment.apiUrl}/snapshot/brand/trend`;
    const payload = {
      ...criteria,
      factTypes,
    };
    return this.http.post<Result<Record<SnapshotDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }

  getItemTrendChart(criteria: SnapshotItem, factTypes: string[]): Observable<Record<SnapshotDimType, EchartData>> {
    const url = `${environment.apiUrl}/snapshot/item/trend`;
    const payload = {
      ...criteria,
      factTypes,
    };
    return this.http.post<Result<Record<SnapshotDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }

  getSelfItemTrendChart(criteria: SnapshotItem): Observable<EchartData> {
    const url = `${environment.apiUrl}/snapshot/selfselected/item/trend`;
    return this.http.post<Result<EchartData>>(url, criteria).pipe(map(res => res.data));
  }

  getSelfItemBpr(criteria: SnapshotItem): Observable<any> {
    const url = `${environment.apiUrl}/snapshot/selfselected/item/bpr`;
    return this.http.post<Result<any>>(url, criteria).pipe(map(res => res.data));
  }

  getSelfItemAvgdpr(criteria: SnapshotItem): Observable<any> {
    const url = `${environment.apiUrl}/snapshot/selfselected/item/avgdpr`;
    return this.http.post<Result<any>>(url, criteria).pipe(map(res => res.data));
  }

  getApiItemTrendChart(criteria: SnapshotItem, factTypes: string[]): Observable<Record<SnapshotDimType, EchartData>> {
    const url = `${environment.apiUrl}/snapshot/apiitem/trend`;
    const payload = {
      ...criteria,
      factTypes,
    };
    return this.http.post<Result<Record<SnapshotDimType, EchartData>>>(url, payload).pipe(map(res => res.data));
  }

  getMarketsSummary(criteria: ItemSearchCriteria, facts: string[], isSaveAction?: boolean): Observable<Array<MktSummary>> {
    const url = `${environment.apiUrl}/chart/summary`;
    const payload = {
      ...criteria,
      factTypes: facts,
    };
    // if (isSaveAction) {
    //   const lastActionTpl = this.lastTplAction;
    //   lastActionTpl.summary = { factTypes: facts };
    //   this.saveLastTplAction(lastActionTpl);
    // }

    return this.http.post<Result<Array<MktSummary>>>(url, payload).pipe(map(res => res.data));
  }

  getProvinces(): Observable<Array<mbdInfo>> {
    const url = `${environment.apiUrl}/mkt/provinces`;
    return this.http.get<Result<Array<mbdInfo>>>(url).pipe(map(res => res.data));
  }

  getManus(payload: ItemSearchCriteria): Observable<Array<string>> {
    const url = `${environment.apiUrl}/tpl/manus`;
    return this.http.post<Result<Array<string>>>(url, payload).pipe(map(res => res.data));
  }

  getBrands(payload: ItemSearchCriteria): Observable<Array<Brand>> {
    const url = `${environment.apiUrl}/brands/list`;
    return this.http.post<Result<Array<Brand>>>(url, payload).pipe(map(res => res.data));
  }
  getBrandsByTemplateId(templateId: string): Observable<string[]> {
  const url = `${environment.apiUrl}/brands/${templateId}/list`;
  return this.http.get<Result<string[]>>(url).pipe(map(res => res.data));
}
  getSubBrands(templateId: string, brands: string[]): Observable<Array<AttrItem>> {
    const url = `${environment.apiUrl}/brands/sub/list`;
    return this.http.post<Result<Array<AttrItem>>>(url, { templateId, brands }).pipe(map(res => res.data));
  }

  getPriceTiers(templateId: string, mbds: string[]): Observable<PriceTier[]> {
    const url = `${environment.apiUrl}/priceTier/list`;
    return this.http.post<Result<Array<PriceTier>>>(url, { templateId, market: mbds }).pipe(map(res => res.data));
  }

  getSegments(templateId: string): Observable<Record<string, Array<AttrItem>>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/segments`;
    return this.http.get<Result<Record<string, Array<AttrItem>>>>(url).pipe(map(res => res.data));
  }

  getPacksizes(templateId: string): Observable<Record<string, Array<AttrItem>>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/packsizes`;
    return this.http.get<Result<Record<string, Array<AttrItem>>>>(url).pipe(map(res => res.data));
  }

  getSummaryInfo(criteria: ItemSearchCriteria): Observable<Result<Array<SummaryItem>>> {
    const url = `${environment.apiUrl}/item/summary`;
    const payload = {
      ...criteria,
    };
    return this.http.post<Result<Array<SummaryItem>>>(url, payload);
  }

  getETailerBrands(templateId: string): Observable<Array<EtailerBrand>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/ebrands`;
    return this.http.get<Result<Array<EtailerBrand>>>(url).pipe(map(res => res.data));
  }

  getETailerBrandsTree(templateId: string): Observable<Array<NzTreeNodeOptions>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/ebrandstree`;
    return this.http.get<Result<Array<NzTreeNodeOptions>>>(url).pipe(map(res => res.data));
  }
  getBrandMappingAndRules(templateId: string): Observable<Array<CodedBrandRule>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/cbrands`;
    return this.http.get<Result<Array<CodedBrandRule>>>(url).pipe(map(res => res.data));
  }

  fillInEbrandMapping(payload: CodedBrandRule) {
    if (!payload.ebrandGroup && payload.pltEbrandList) {
      payload.ebrandGroup = payload.pltEbrandList.map(ele => {
        let brandName = payload.brandName;
        let platform: any = null;
        let ebrandName: any = null;
        if (ele) {
          const arr = ele.split('##');
          brandName = arr[0];
          platform = arr[1];
          ebrandName = arr[2];
        }
        return {
          brandName,
          platform,
          ebrandName,
          pltEbrand: ele,
        };
      });
    }
  }
  validateBrandMapping(payload: CodedBrandRule): Observable<Array<EtailerBrand>> {
    const url = `${environment.apiUrl}/tpl/validateBrandMapping`;
    this.fillInEbrandMapping(payload);
    return this.http.post<Result<Array<EtailerBrand>>>(url, payload).pipe(map(res => res.data));
  }

  addBrandMapping(payload: CodedBrandRule): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/addBrandMapping`;
    this.fillInEbrandMapping(payload);
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  saveCodedBrandRule(payload: CodedBrandRule): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/saveBrandRule`;
    this.fillInEbrandMapping(payload);
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  executeJob(jobApi: string, jobParams?: Record<string, any>): Observable<any> {
    // 有progress的API： brands/coding, segment/coding, packsize/coding, brands/tag, tpl/saveDefinedSet
    const url = `${environment.apiUrl}/${jobApi}`;
    const payload: Record<string, any> = { ...jobParams };

    return this.http.post<Result<any>>(url, payload).pipe(map(res => res.data));
  }

  getJobProgress(jobType: string, templateId: string): Observable<JobInfo> {
    // jobType: brands, segment, packsize, tagbrands, saveds
    const url = `${environment.apiUrl}/job/${templateId}/${jobType}/codingProgress`;
    return this.http.get<Result<JobInfo>>(url).pipe(map(res => res.data));
  }

  exportTemplate(templateId: string, selectedSheets: string[]): Observable<HttpResponse<Blob>> {
    const params = selectedSheets.map(s => `selectedSheets=${encodeURIComponent(s)}`).join('&');
    const url = `${environment.apiUrl}/tpl/export/${encodeURIComponent(templateId)}?${params}`;
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  importTemplate(templateId: string, file: File, selectedSheets: string[]): Observable<Result<{ templateId: string; templateName: string; hints: string[] }>> {
    const url = `${environment.apiUrl}/tpl/${encodeURIComponent(templateId)}/import`;
    const formData = new FormData();
    formData.append('file', file);
    selectedSheets.forEach(s => formData.append('selectedSheets', s));
    return this.http.post<Result<{ templateId: string; templateName: string; hints: string[] }>>(url, formData);
  }

  getSegmentsCfg(templateId: string, segType?: string): Observable<Array<SegmentConfig>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/${!!segType ? segType + '/' : ''}segmentsCfg`;
    return this.http.get<Result<Array<SegmentConfig>>>(url).pipe(map(res => res.data));
  }
  getTemplateSegmentsCfg(templateId: string, segType?: string): Observable<Array<SegmentConfig>> {
    const url = `${environment.apiUrl}/tpl/${templateId}/${!!segType ? segType + '/' : ''}allSegmentsCfg`;
    return this.http.get<Result<Array<SegmentConfig>>>(url).pipe(map(res => res.data));
  }
  addSegment(templateId: string, segmentName: string, segmentValues?: Array<string>, codingScope?: Array<string>): Observable<number> {
    const url = `${environment.apiUrl}/tpl/segment/add`;
    const payload = {
      templateId,
      segLabel: segmentName,
      segValues: segmentValues || [],
      codingScope: codingScope || [],
    };
    return this.http.post<Result<number>>(url, payload).pipe(map(res => res.data));
  }

  updateSegment(templateId: string, segmentId: number, segmentName: string, codingScope?: Array<string>): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/segment/update`;
    const payload = {
      templateId,
      segId: segmentId,
      segLabel: segmentName,
      codingScope: codingScope || [],
    };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  deleteSegment(templateId: string, segmentId: number): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/${templateId}/delSegment/${segmentId}`;
    return this.http.delete<Result<boolean>>(url).pipe(map(res => res.data));
  }

  updateSegmentOrder(templateId: string, segmentIds: Array<number>): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/${templateId}/segment/updateOrder`;
    return this.http.post<Result<boolean>>(url, segmentIds).pipe(map(res => res.data));
  }

  addSegmentValue(templateId: string, segmentId: number, segmentValue: string): Observable<number> {
    const url = `${environment.apiUrl}/tpl/segCfg/add`;
    const payload = {
      templateId,
      segId: segmentId,
      segValueLabel: segmentValue,
    };
    return this.http.post<Result<number>>(url, payload).pipe(map(res => res.data));
  }

  updateSegmentValue(templateId: string, segmentId: number, segValueId: number, newSegmentValue: string): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/segCfg/update`;
    const payload = { templateId, segId: segmentId, segValueId, segValueLabel: newSegmentValue };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  updateSegmentKwCfg(
    templateId: string,
    segmentId: number,
    segValueId: number,
    keywordRules?: Array<Array<KeywordRule>>,
    codingScope?: string[],
  ): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/segCfg/update`;
    const payload = { templateId, segId: segmentId, segValueId, keywordRules: keywordRules || [], codingScope };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  deleteSegmentConfig(templateId: string, segmentId: number, segValueId: number): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/${templateId}/${segmentId}/delSegCfg/${segValueId}`;
    return this.http.delete<Result<boolean>>(url).pipe(map(res => res.data));
  }

  updateSegValuePriority(templateId: string, segmentId: number, segmentValueIds: (number | undefined)[]): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/${templateId}/${segmentId}/updSegValPrior`;
    return this.http.post<Result<boolean>>(url, segmentValueIds).pipe(map(res => res.data));
  }

  getCatesForPacksize(templateId: string): Observable<Array<OptionItem>> {
    const url = `${environment.apiUrl}/category/pks?tplid=${templateId}`;
    return this.http.get<Result<Array<OptionItem>>>(url).pipe(map(res => res.data));
  }

  downloadSkuItems(criteria: ItemSearchCriteria): Observable<boolean> {
    const url = `${environment.apiUrl}/item/downloadSkuItem/search`;
    return this.http.post<Result<boolean>>(url, criteria).pipe(map(res => res.data));
  }

  downloadDetail(criteria: ItemSearchCriteria): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/item/download/search`;
    return this.callDownload(url, criteria);
  }

  downloadHotword(criteria: ItemSearchCriteria): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/hotword/download`;
    return this.callDownload(url, criteria);
  }

  createHotwordDownloadTask(criteria: ItemSearchCriteria): Observable<boolean> {
    const url = `${environment.apiUrl}/download/createHotwordDownloadTask`;
    return this.http.post<Result<boolean>>(url, criteria).pipe(map(res => res.data));
  }

  // downloadStore(criteria: ItemSearchCriteria): Observable<HttpResponse<Blob>> {
  //   const url = `${environment.apiUrl}/item/download/store_summary`;
  //   return this.callDownload(url, criteria);
  // }
  downloadStore(criteria: ItemSearchCriteria): Observable<boolean> {
    const url = `${environment.apiUrl}/item/downloadStore/store_summary`;
    return this.http.post<Result<boolean>>(url, criteria).pipe(map(res => res.data));
  }

  downloadStoreDetail(criteria: ItemSearchCriteria): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/item/download/store_item`;
    return this.callDownload(url, criteria);
  }
  //下载新流程
  downloadStoreInfo(criteria: ItemSearchCriteria): Observable<Boolean> {
    const url = `${environment.apiUrl}/item/downloadSkuItem/store_item`;
    return this.http.post<Result<boolean>>(url, criteria).pipe(map(res => res.data));
  }
  downloadSummary(criteria: ItemSearchCriteria, factTypes: FactKey[]): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/chart/download/summary`;
    const payload = { ...criteria, factTypes };
    return this.callDownload(url, payload);
  }

  downloadTopsku(criteria: ItemSearchCriteria, factTypes: FactKey[]): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/topsku/download`;
    const payload = { ...criteria, factTypes };
    return this.callDownload(url, payload);
  }

  downloadSnapshot(templateId: string, platform: string, weekCodes: string[]): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/snapshot/download`;
    const payload = { templateId, platform, weekCodes };
    return this.callDownload(url, payload);
  }
  downloadPrices(templateId: string, days: string[], keyTitleMap: any, isDownloadDaily: boolean): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/price/tracking/ssiList/download`;
    const payload = { templateId, days, keyTitleMap, isDownloadDaily };
    return this.callDownload(url, payload);
  }
  downloadSelfselect(templateId: string): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/price/tracking/ssiList/template/download`;
    const payload = { templateId };
    return this.callDownload(url, payload);
  }

  downloadPricesTrend(payload: any): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/price/tracking/ssiTrend/download`;
    return this.callDownload(url, payload);
  }

  downloadKolList(payload: kolCriteria): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/download/kols`;
    return this.callDownload(url, payload);
  }
  createKOLDownload(payload: kolCriteria): Observable<Boolean>{
    const url = `${environment.apiUrl}/download/createKolAssessmentDownloadTask`;
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }
  createKOLBrandsDownload(payload: kolCriteria): Observable<Boolean>{
    const url = `${environment.apiUrl}/download/createKolBrandsDownloadTask`;
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }
  downloadKolBrandList(payload: kolCriteria): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/download/kols/brands`;
    return this.callDownload(url, payload);
  }

  downloadTrend(criteria: ItemSearchCriteria, factTypes: string[], isSplitByPlt: boolean): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiUrl}/download/trend`;
    const payload = { ...criteria, factTypes, isSplitByPlt };
    return this.callDownload(url, payload);
  }
  createStoreTrendDownloadTask(criteria: ItemSearchCriteria, factTypes: string[], isSplitByPlt: boolean): Observable<Boolean> {
    const url = `${environment.apiUrl}/download/createTrendDownloadTask/store_trend`;
    const payload = { ...criteria, factTypes, isSplitByPlt };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }
  createTrendDownloadTask(criteria: ItemSearchCriteria, factTypes: string[], isSplitByPlt: boolean): Observable<Boolean> {
    const url = `${environment.apiUrl}/download/createTrendDownloadTask/trend`;
    const payload = { ...criteria, factTypes, isSplitByPlt };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  callDownload(url: string, payload: any): Observable<HttpResponse<Blob>> {
    return this.http.post(url, payload, {
      responseType: 'blob',
      observe: 'response',
      headers: { 'Content-Type': 'application/octet-stream;charset=UTF-8' },
    });
  }

  getRunningJobs(templateId: string): Observable<JobInfo[]> {
    const url = `${environment.apiUrl}/job/${templateId}/runningJobs`;
    return this.http.get<Result<JobInfo[]>>(url).pipe(map(res => res.data));
  }

  getCodingJobs(templateId: string): Observable<JobInfo[]> {
    const codingTasks = [ProgressType.BRD_CODING, ProgressType.BRD_TAGGING, ProgressType.SEG_CODING, ProgressType.PKS_CODING];
    return this.getRunningJobs(templateId).pipe(
      map(data => {
        return data.filter(job => codingTasks.includes(job.codingType));
      }),
    );
  }

  getAuthFacts(templateId: string): Observable<Array<FactInfo>> {
    const url = `${environment.apiUrl}/facts/authList/${templateId}`;
    return this.http.get<Result<Array<FactInfo>>>(url).pipe(map(res => res.data));
  }

  getKolFilters(templateId: string): Observable<Record<FilterDimType, OptionItem[]>> {
    const url = `${environment.apiUrl}/kol/${templateId}/filters`;
    return this.http.get<Result<Record<FilterDimType, OptionItem[]>>>(url).pipe(map(res => res.data));
  }

  getKolAllMetrics(payload: kolCriteria): Observable<Record<KolMetricsType, KolMetrics[]>> {
    const url = `${environment.apiUrl}/kol/allMetrics`;
    return this.http.post<Result<Record<KolMetricsType, KolMetrics[]>>>(url, payload).pipe(map(res => res.data));
  }

  getKolFacts(moduleType: string): Observable<Array<FactInfo>> {
    const url = `${environment.apiUrl}/kol/facts?moduleType=${moduleType}`;
    return this.http.get<Result<Array<FactInfo>>>(url).pipe(map(res => res.data));
  }

  kolBasicInfo(payload: kolCriteria): Observable<any> {
    const url = `${environment.apiUrl}/kol/basicInfo`;
    return this.http.post<Result<any>>(url, payload).pipe(map(res => res.data));
  }

  kolAvatarContent(avatarId: string): Observable<any> {
    const url = `${environment.apiUrl}/kol/${avatarId}/getAvatarContent`;
    return this.http.get<Result<any>>(url).pipe(map(res => res.data));
  }

  kolFanPortrait(payload: kolCriteria): Observable<any> {
    const url = `${environment.apiUrl}/kol/fanPortrait`;
    return this.http.post<Result<any>>(url, payload).pipe(map(res => res.data));
  }

  kolPortraitTrend(payload: kolCriteria): Observable<any> {
    const url = `${environment.apiUrl}/kol/portrait/trend`;
    return this.http.post<Result<any>>(url, payload).pipe(map(res => res.data));
  }

  kolDetailProdList(payload: kolCriteria): Observable<Array<KolBrandList>> {
    const url = `${environment.apiUrl}/kol/detail/prod/list`;
    return this.http.post<Result<Array<KolBrandList>>>(url, payload).pipe(map(res => res.data));
  }

  kolDetailBrandCharts(payload: kolCriteria): Observable<Array<KolBrandChart>> {
    const url = `${environment.apiUrl}/kol/detail/brand/chart`;
    return this.http.post<Result<Array<KolBrandChart>>>(url, payload).pipe(map(res => res.data));
  }

  kolOverview(payload: payloadItem): Observable<Array<kolOverview>> {
    const url = `${environment.apiUrl}/kol/overview`;
    return this.http.post<Result<Array<kolOverview>>>(url, payload).pipe(map(res => res.data));
  }

  kolProdSearch(payload: payloadItem): Observable<PagingData<KolProds>> {
    const url = `${environment.apiUrl}/kol/prod/search`;
    return this.http.post<Result<PagingData<KolProds>>>(url, payload).pipe(map(res => res.data));
  }

  kolSearchNames(payload: payloadItem): Observable<Array<{ id: string; name: string }>> {
    const url = `${environment.apiUrl}/kol/searchNames`;
    return this.http.post<Result<Array<{ id: string; name: string }>>>(url, payload).pipe(map(res => res.data));
  }

  allCatKolData(payload: kolCriteria): Observable<Array<allCataData>> {
    const url = `${environment.apiUrl}/kol/stats/onAllCat`;
    return this.http.post<Result<Array<allCataData>>>(url, payload).pipe(map(res => res.data));
  }

  ksCatKolData(payload: kolCriteria): Observable<Array<ksCataData>> {
    const url = `${environment.apiUrl}/kol/stats/onKs`;
    return this.http.post<Result<Array<ksCataData>>>(url, payload).pipe(map(res => res.data));
  }

  tiktokCatKolData(payload: kolCriteria): Observable<Array<tiktokCataData>> {
    const url = `${environment.apiUrl}/kol/stats/onTikTok`;
    return this.http.post<Result<Array<tiktokCataData>>>(url, payload).pipe(map(res => res.data));
  }

  kolBrandStats(payload: kolCriteria): Observable<Array<BrandStats>> {
    const url = `${environment.apiUrl}/kol/brand/brandStats`;
    return this.http.post<Result<Array<BrandStats>>>(url, payload).pipe(map(res => res.data));
  }

  kolBrandChart(payload: kolCriteria): Observable<EchartData> {
    const url = `${environment.apiUrl}/kol/brand/brandKlv/chart`;
    return this.http.post<Result<EchartData>>(url, payload).pipe(map(res => res.data));
  }

  /*getSummaryFactOptions(factList: FactInfo[], currLang: LocaleLang): OptionItem[] {
    const options = factList
      .filter(item => item.showInSummary)
      .map(
        fact =>
          ({
            optionKey: fact.factKey,
            optionLabel: fact?.factName[currLang],
          } as OptionItem),
      );
    return options;
  }
  getTrendFactOptions(factList: FactInfo[], currLang: LocaleLang): OptionItem[] {
    const groupedFacts: Record<number, FactInfo[]> = groupBy(factList, 'factGroup');
    const options: OptionItem[] = factList
      .filter(fact => fact.showInTrend)
      .map(fact => {
        const key = groupedFacts[fact.factGroup].map(item => item.factKey).join(',');
        return {
          optionKey: key,
          optionLabel: fact?.factName[currLang],
        } as OptionItem;
      });
    return options;
  }*/

  getFactOptions(factList: FactInfo[], currLang: LocaleLang): OptionItem[] {
    const groupedFacts: Record<number, FactInfo[]> = groupBy(factList, 'factGroup');
    const options: OptionItem[] = factList.map(fact => {
      const key = groupedFacts[fact.factGroup].map(item => item.factKey).join(',');
      return {
        optionKey: key,
        optionLabel: fact?.factName[currLang],
        tooltips: fact?.extraInfo?.tooltips,
      } as OptionItem;
    });
    return options;
  }
  getFactOptionsWithoutGroup(factList: FactInfo[], currLang: LocaleLang): OptionItem[] {
    const options: OptionItem[] = factList.map(fact => {
      return {
        optionKey: fact.factKey,
        optionLabel: fact?.factName[currLang],
        tooltips: fact?.extraInfo?.tooltips,
      } as OptionItem;
    });
    return options;
  }
  getFactWithoutGroup(factList: FactInfo[], currLang: LocaleLang): OptionItem[] {
    const options: OptionItem[] = factList.map(fact => {
      return {
        optionKey: fact.factKey,
        optionLabel: fact?.factName[currLang],
      } as OptionItem;
    });
    return options;
  }

  getHotWordFactOptions(factList: FactInfo[], currLang: LocaleLang): OptionItem[] {
    const groupedFacts: Record<number, FactInfo[]> = groupBy(factList, 'factGroup');
    const options: OptionItem[] = factList.map(fact => {
      const key = groupedFacts[fact.factGroup].map(item => item.factKey).join(',');
      return {
        optionKey: key,
        optionLabel: fact?.factName[currLang],
      } as OptionItem;
    });
    return options;
  }

  getTopSKUTreeList(
    criteria: ItemSearchCriteria,
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
  ): Observable<PagingData<TopskuItem>> {
    const url = `${environment.apiUrl}/topsku/search`;
    const payload = {
      ...criteria,
      pageIndex,
      pageSize,
      sortDirection: sortOrder === 'ascend' ? 'ASC' : sortOrder === 'descend' ? 'DESC' : null,
      sortColumn: sortField,
    };
    return this.http.post<Result<PagingData<TopskuItem>>>(url, payload).pipe(map(res => res.data));
  }

  deleteTpl(templateId: string): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/cleanup/${templateId}`;
    return this.http.delete<Result<boolean>>(url).pipe(map(res => res.data));
  }

  getShareableUsers(): Observable<OptionItem[]> {
    const url = `${environment.apiUrl}/user/shareable`;
    return this.http.get<Result<User[]>>(url).pipe(
      map(res => {
        const options: OptionItem[] = res.data.map(item => {
          return {
            optionKey: `${item.userId}`,
            optionLabel: item.username,
          } as OptionItem;
        });
        return options;
      }),
    );
  }

  shareToUsers(templateIds: string[], userIds: number[]): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/shareToUsers`;
    const payload = { templateIds, userIds };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  shareToGroups(templateIds: string[], groupIds: number[]): Observable<boolean> {
    const url = `${environment.apiUrl}/tpl/shareToGroups`;
    const payload = { templateIds, groupIds };
    return this.http.post<Result<boolean>>(url, payload).pipe(map(res => res.data));
  }

  getAllECCates(): Observable<Cate[]> {
    const url = `${environment.apiUrl}/category/${ImpDataType.EC_CATE}/list`;
    return this.http.get<Result<Cate[]>>(url).pipe(map(res => res.data));
  }

  getCateAttrs(cateCode: string): Observable<CateAttribute[]> {
    const url = `${environment.apiUrl}/category/${cateCode}/attributes`;
    return this.http.get<Result<CateAttribute[]>>(url).pipe(map(res => res.data));
  }

  getPyBrands() {
    const url = '/kc_results_summary';
    const payload = {
      coding_rule_id: 1,
      coding_rule_name: '婴儿配方奶',
      inex_0: '',
      txt_0: '婴儿|幼儿',
      inex_1: '',
      txt_1: '配方',
      inex_2: '',
      txt_2: '1段|2段|3段|4段|一段|二段|三段|四段',
      inex_3: '',
      txt_3: '美赞臣|惠氏|爱他美|美素佳儿|飞鹤|伊利|雅培|君乐宝|合生元|启赋|金领冠|诺优能|贝因美|可瑞康',
      type: 'cats',
      cats_1: '婴幼儿奶粉',
      cats_2: '婴幼儿奶粉',
      cats_4: '奶粉/辅食/营养品',
      cats_5: '特殊配方奶粉',
    };
    return this.http.post(url, qs.stringify(payload)).pipe(
      map((res: any) => {
        return res.brands;
      }),
    );
  }

  getPySummarizeInfo() {
    const url = '/kc_aggregation';
    const payload = { by_type: 'by_t_cat' };
    return this.http.post(url, qs.stringify(payload), { responseType: 'text' });
  }

  submitSplitItem(items: any): Observable<boolean> {
    const url = `${environment.apiUrl}/bundleCoding/submitSplitItem`;
    return this.http.post<Result<boolean>>(url, items).pipe(map(res => res.data));
  }
  submitSplitItems(criteria: ItemSearchCriteria, splitNum: any): Observable<boolean> {
    const url = `${environment.apiUrl}/bundleCoding/${splitNum}/submitSplitItems`;
    return this.http.post<Result<boolean>>(url, criteria).pipe(map(res => res.data));
  }
  queryResults(request: any): Observable<any> {
    const url = `${environment.apiUrl}/bundleCoding/queryResults`;
    return this.http.post<Result<any>>(url, request).pipe(map(res => res.data));
  }
  querySummaryResults(request: any): Observable<any> {
    const url = `${environment.apiUrl}/bundleCoding/querySummaryResults`;
    return this.http.post<Result<any>>(url, request).pipe(map(res => res.data));
  }
  queryBatchItemsCount(request: any): Observable<any> {
    const url = `${environment.apiUrl}/bundleCoding/queryBatchItemsCount`;
    return this.http.post<Result<any>>(url, request).pipe(map(res => res.data));
  }
  queryCategoryPrompt(request: any): Observable<any> {
    const url = `${environment.apiUrl}/bundleCoding/queryCategoryPrompt`;
    return this.http.post<Result<any>>(url, request).pipe(map(res => res.data));
  }
  getTemplateCategory(templateId: any): Observable<any> {
    const url = `${environment.apiUrl}/tpl/${templateId}/category`;
    return this.http.get<Result<any>>(url).pipe(map(res => res.data));
  }
}
